import {useTranslation} from "react-i18next";
import {Navigate, Route, Routes, useNavigate} from "react-router-dom";
import NotFound from "./pages/error/NotFound.jsx";
import SignIn from "./pages/auth/SignIn.jsx";
import SignOut from "./pages/auth/SignOut.jsx";
import McpAuthorizationConsent from "./pages/auth/McpAuthorizationConsent.jsx";
import {useContext} from "react";
import SignUp from "./pages/auth/SignUp.jsx";
import AddonAuth from "./pages/auth/AddonAuth.jsx";
import NoProjectScreen from "./pages/NoProjectScreen.jsx";
import UserProjectList from "./pages/userSettings/UserProjectList.jsx";
import {GlobalContext} from "./utils/context/GlobalContextProvider.jsx";
import ProjectSettings from "./pages/project/ProjectSettings.jsx";
import {getProjectSettingsAccess} from "./pages/project/ThirdPartyServiceIntegrations/ProjectMcpGrants.jsx";
import ProjectStep from "./pages/auth/OnBoarding/Steps/ProjectStep.jsx";
import AccountNameStep from "./pages/auth/OnBoarding/Steps/AccountNameStep.jsx";
import ResetPassword from "./pages/auth/ResetPassword.jsx";
import ProjectUsers from "./pages/project/ProjectUsers.jsx";
import ContinueFromEmail from "./pages/auth/ContinueFromEmail.jsx";
import ThirdPartyServiceIntegrations
    from "./pages/project/ThirdPartyServiceIntegrations/ThirdPartyServiceIntegrations.jsx";
import CreateIntegrationChoosePlatform
    from "./pages/project/ThirdPartyServiceIntegrations/CreateIntegrationChoosePlatform.jsx";
import MainLeadOverview from "./pages/dashboard/LeadDashboard/MainLeadOverview.jsx";
import MainOrderOverview from "./pages/dashboard/OrderDashboard/MainOrderOverview.jsx";
import DailyOverview from "./pages/dashboard/OrderDashboard/DailyOverview.jsx";
import DiscountAndCouponOverview from "./pages/dashboard/OrderDashboard/DiscountAndCouponOverview.jsx";
import OrderMarketingChannelOverview from "./pages/dashboard/OrderDashboard/OrderMarketingChannelOverview.jsx";
import SignUpGoToEmail from "./pages/auth/SignUpGoToEmail.jsx";
import ContinueFromInvitation from "./pages/auth/ContinueFromInvitation.jsx";
import SetPasswordStep from "./pages/auth/OnBoarding/Steps/SetPasswordStep.jsx";
import AddonProjectStep from "./pages/auth/OnBoarding/Steps/AddonProjectStep.jsx";
import CampaignPerformance from "./pages/dashboard/OrderDashboard/CampaignPerformance.jsx";
import {OtherSystemType, ProjectLegacyReport, ProjectPermission, ProjectReport} from "./api/authority/generated/index";
import {hasSubscriptionCostSource, hasSubscriptionFeature} from "./utils/subscriptionFeatures.js";
import {administratorOnlyRoute, markRoutesAdministratorOnly} from "./utils/routeAccess.js";
import OtherSystemSettings from "./pages/project/OtherSystems/OtherSystemSettings.jsx";
import ProjectRoles from "./pages/project/Roles/ProjectRoles.jsx";
import {SensitiveMetric, useHasLegacyReport, useHasPermission, useHasReport, useHasSensitiveMetric, useIsAdministrator} from "./components/auth/rolesAndPermissions.js";
import ProjectSubscription from "./pages/project/ProjectSubscription.jsx";
import OrderMarketingProfitabilityOverview
    from "./pages/dashboard/OrderDashboard/OrderMarketingProfitabilityOverview.jsx";
import LeadMarketingChannelOverview from "./pages/dashboard/LeadDashboard/LeadMarketingChannelOverview.jsx";
import {useBrands} from "./utils/brands/branding.js";
import ProjectDomains from "./pages/project/Domains/ProjectDomains.jsx";
import OrderStatusSettings from "./pages/project/ThirdPartyServiceIntegrations/OrderStatusSettings.jsx";
import ServiceStatus from "./pages/admin/ServiceStatus.jsx";
import SubscriptionPackages from "./pages/admin/SubscriptionPackages.jsx";
import SubscriptionPayments from "./pages/admin/SubscriptionPayments.jsx";
import NewComponents from "./pages/admin/NewComponents.jsx";
import BasketIcon from "./images/remixicons/Finance/shopping-basket-2-line.js";
import AdIcon from "./images/remixicons/Business/advertisement-line.js";
import MoneyIcon from "./images/remixicons/Finance/money-dollar-circle-line.js";
import SettingsIcon from "./images/remixicons/Media/equalizer-line.js";
import BookIcon from "./images/remixicons/Document/book-3-line.js";
import HelpdeskIcon from "./images/remixicons/Finance/coupon-line.js";
import ShieldUserIcon from "./images/remixicons/System/shield-user-line.js";
import UsersIcon from "./images/remixicons/UserFaces/group-2-line.js";
import DashboardIcon from "./images/remixicons/Device/dashboard-3-line.js";
import ChartIcon from "./images/remixicons/Business/bar-chart-box-line.js";
import LinksIcon from "./images/remixicons/Business/links-line.js";
import HistoryIcon from "./images/remixicons/System/history-fill.js";
import SvgIcon from "./components/SvgIcon.jsx";
import {isPreview} from "./utils/environment.js";
import PowerBiDashboard from "./pages/dashboard/PowerBiDashboard/PowerBiDashboard.jsx";
import ReportingLab from "./pages/admin/ReportingLab.jsx";
import ReportingMarketingLab from "./pages/admin/ReportingMarketingLab.jsx";
import ReportingProductsLab from "./pages/admin/ReportingProductsLab.jsx";
import ReportingProfitabilityLab from "./pages/admin/ReportingProfitabilityLab.jsx";
import ReportingCampaignsLab from "./pages/admin/ReportingCampaignsLab.jsx";
import ReportingDiscountsLab from "./pages/admin/ReportingDiscountsLab.jsx";
import ReportingDeliveryPaymentsLab from "./pages/admin/ReportingDeliveryPaymentsLab.jsx";
import ReportingOrdersLab from "./pages/admin/ReportingOrdersLab.jsx";
import ReportingPeriodComparisonLab from "./pages/admin/ReportingPeriodComparisonLab.jsx";
import ReportingDomainsLab from "./pages/admin/ReportingDomainsLab.jsx";
import ReportingCentresLab from "./pages/admin/ReportingCentresLab.jsx";
import ReportingOtherCostsLab from "./pages/admin/ReportingOtherCostsLab.jsx";
import ReportingSalesDiscountsLab from "./pages/admin/ReportingSalesDiscountsLab.jsx";
import ReportingPnoLab from "./pages/admin/ReportingPnoLab.jsx";
import ReportingDataQualityLab from "./pages/admin/ReportingDataQualityLab.jsx";
import ReportingDailyLab from "./pages/admin/ReportingDailyLab.jsx";
import ReportingMarketingTrendsLab from "./pages/admin/ReportingMarketingTrendsLab.jsx";
import ReportingAbcLab from "./pages/admin/ReportingAbcLab.jsx";
import ReportingCategoriesBrandsLab from "./pages/admin/ReportingCategoriesBrandsLab.jsx";
import ReportingProductProfitabilityLab from "./pages/admin/ReportingProductProfitabilityLab.jsx";
import ReportingTrackingQualityLab from "./pages/admin/ReportingTrackingQualityLab.jsx";
import ReportingAdvertisingClassificationLab, {AdvertisingReportView} from "./pages/admin/ReportingAdvertisingClassificationLab.jsx";
import ReportingGoogleDataLab, {GoogleDataReportView} from "./pages/admin/ReportingGoogleDataLab.jsx";
import {FilterContext} from "./components/filters/DashboardFilterContextProvider.jsx";

export function useProjectRoutes(currentOnly = false) {
    const {t} = useTranslation();

    let {context, getAuthorityConfiguration} = useContext(GlobalContext);
    let {projectCentresCount = 0, projectDomainsCount = 0} = useContext(FilterContext) ?? {};

    let brands = useBrands();

    let hasPermission = useHasPermission();
    let hasReport = useHasReport();
    let hasLegacyReport = useHasLegacyReport();
    let hasSensitiveMetric = useHasSensitiveMetric();
    let isAdministrator = useIsAdministrator();

    let currentProject = null;

    for (let possibleProject of context.currentUserProjects) {
        if (possibleProject.id === context.currentProjectId) {
            currentProject = possibleProject;
        }
    }

    let routes = [];

    let lastProjectName = t('project.project');

    let defaultRouteSelected = false;

    /** @var ProjectData project */
    for (let project of context.currentUserProjects) {
        let legacyReportRoutes = [];

        let isCurrent = false;

        // noinspection JSUnresolvedReference
        if (project.id === context.currentProjectId) {
            // noinspection JSUnresolvedReference
            lastProjectName = project.name;
            isCurrent = true;
            project = {...currentProject, current: true};
        } else if (currentOnly) {
            continue;
        }

        if (project.id === '30704256-1cdc-493f-b5e0-8871a30885c2') {
            routes.push({
                path: defaultRouteSelected ? '/project/' + project.id + '/powerbi' : '/',
                name: 'Power BI',
                icon: <SvgIcon source={ChartIcon} />,
                page: <PowerBiDashboard project={project}/>
            });
            defaultRouteSelected = true;
        }

        [
                {
                    report: ProjectReport.ReportingOverview,
                    path: '/project/' + project.id + '/reporting',
                    name: t("reporting_lab.pages.overview", {defaultValue: "Celkové shrnutí"}),
                    group: t('route.sales-performance'),
                    icon: <SvgIcon source={BasketIcon} />,
                    page: <ReportingLab project={project}/>
                },
                {
                    report: ProjectReport.ReportingDaily,
                    path: '/project/' + project.id + '/reporting/daily',
                    name: t("reporting_lab.pages.daily", {defaultValue: "Denní výkon"}),
                    group: t('route.sales-performance'),
                    icon: <SvgIcon source={BasketIcon} />,
                    page: <ReportingDailyLab project={project}/>
                },
                {
                    report: ProjectReport.ReportingPeriodComparison,
                    path: '/project/' + project.id + '/reporting/period-comparison',
                    name: t("reporting_lab.pages.period_comparison", {defaultValue: "Srovnání období"}),
                    group: t('route.sales-performance'),
                    icon: <SvgIcon source={BasketIcon} />,
                    page: <ReportingPeriodComparisonLab project={project}/>
                },
                {
                    report: ProjectReport.ReportingOrders,
                    path: '/project/' + project.id + '/reporting/orders',
                    name: t("reporting_lab.pages.orders", {defaultValue: "Objednávky a storna"}),
                    group: t('route.sales-performance'),
                    icon: <SvgIcon source={BasketIcon} />,
                    page: <ReportingOrdersLab project={project}/>
                },
                {
                    report: ProjectReport.ReportingMarketing,
                    path: '/project/' + project.id + '/reporting/marketing',
                    name: t("reporting_lab.pages.marketing", {defaultValue: "Marketing"}),
                    group: t('route.marketing'),
                    icon: <SvgIcon source={AdIcon} />,
                    page: <ReportingMarketingLab project={project}/>
                },
                {
                    report: ProjectReport.ReportingMarketingTrends,
                    path: '/project/' + project.id + '/reporting/marketing-trends',
                    name: t("reporting_lab.pages.marketing_trends", {defaultValue: "Marketingové trendy"}),
                    group: t('route.marketing'),
                    icon: <SvgIcon source={AdIcon} />,
                    page: <ReportingMarketingTrendsLab project={project}/>
                },
                {
                    report: ProjectReport.ReportingPno,
                    path: '/project/' + project.id + '/reporting/pno',
                    name: t("reporting_lab.pages.pno", {defaultValue: "PNO a marketingový mix"}),
                    group: t('route.marketing'),
                    icon: <SvgIcon source={AdIcon} />,
                    page: <ReportingPnoLab project={project}/>
                },
                {
                    report: ProjectReport.ReportingCampaigns,
                    path: '/project/' + project.id + '/reporting/campaigns',
                    name: t("reporting_lab.pages.campaigns", {defaultValue: "Kampaně"}),
                    group: t('route.marketing'),
                    icon: <SvgIcon source={AdIcon} />,
                    page: <ReportingCampaignsLab project={project}/>
                },
                {
                    report: ProjectReport.ReportingProducts,
                    path: '/project/' + project.id + '/reporting/products',
                    name: t("reporting_lab.pages.products", {defaultValue: "Produkty"}),
                    group: t('route.sales-performance'),
                    icon: <SvgIcon source={BasketIcon} />,
                    page: <ReportingProductsLab project={project}/>
                },
                {
                    report: ProjectReport.ReportingAbc,
                    subscriptionFeature: "abc_analysis",
                    path: '/project/' + project.id + '/reporting/abc',
                    name: t("reporting_lab.pages.abc", {defaultValue: "ABC analýza"}),
                    group: t('route.sales-performance'),
                    icon: <SvgIcon source={BasketIcon} />,
                    page: <ReportingAbcLab project={project}/>
                },
                {
                    report: ProjectReport.ReportingProductProfitability,
                    requiresSensitiveMetric: SensitiveMetric.Profit,
                    path: '/project/' + project.id + '/reporting/product-profitability',
                    name: t("reporting_lab.pages.product_profitability", {defaultValue: "Produktová ziskovost"}),
                    group: t('route.sales-performance'),
                    icon: <SvgIcon source={BasketIcon} />,
                    page: <ReportingProductProfitabilityLab project={project}/>
                },
                {
                    report: ProjectReport.ReportingProfitability,
                    requiresSensitiveMetric: SensitiveMetric.Profit,
                    path: '/project/' + project.id + '/reporting/profitability',
                    name: t("reporting_lab.pages.profitability", {defaultValue: "Ziskovost"}),
                    group: t('route.sales-performance'),
                    icon: <SvgIcon source={BasketIcon} />,
                    page: <ReportingProfitabilityLab project={project}/>
                },
                {
                    report: ProjectReport.ReportingDiscounts,
                    path: '/project/' + project.id + '/reporting/discounts',
                    name: t("reporting_lab.pages.discounts", {defaultValue: "Slevy a kupony"}),
                    group: t('route.marketing'),
                    icon: <SvgIcon source={AdIcon} />,
                    page: <ReportingDiscountsLab project={project}/>
                },
                {
                    report: ProjectReport.ReportingDeliveryPayments,
                    path: '/project/' + project.id + '/reporting/delivery-payments',
                    name: t("reporting_lab.pages.delivery_payments", {defaultValue: "Doprava a platby"}),
                    group: t('route.sales-performance'),
                    icon: <SvgIcon source={BasketIcon} />,
                    page: <ReportingDeliveryPaymentsLab project={project}/>
                },
                {
                    report: ProjectReport.ReportingDomains,
                    show: !isCurrent || projectDomainsCount > 1,
                    path: '/project/' + project.id + '/reporting/domains',
                    name: t("reporting_lab.pages.domains", {defaultValue: "Domény"}),
                    group: t('route.sales-performance'),
                    icon: <SvgIcon source={BasketIcon} />,
                    page: <ReportingDomainsLab project={project}/>
                },
                {
                    report: ProjectReport.ReportingCentres,
                    show: !isCurrent || projectCentresCount > 1,
                    path: '/project/' + project.id + '/reporting/centres',
                    name: t("reporting_lab.pages.centres", {defaultValue: "Střediska"}),
                    group: t('route.sales-performance'),
                    icon: <SvgIcon source={BasketIcon} />,
                    page: <ReportingCentresLab project={project}/>
                },
                {
                    report: ProjectReport.ReportingOtherCosts,
                    subscriptionCostSource: "other_costs",
                    path: '/project/' + project.id + '/reporting/other-costs',
                    name: t('route.costs-overview'),
                    group: t('route.costs'),
                    icon: <SvgIcon source={MoneyIcon} />,
                    page: <ReportingOtherCostsLab project={project}/>
                },
            ]
                .filter((route) => hasReport(route.report, project)
                    && (route.show ?? true)
                    && (route.requiresSensitiveMetric === undefined || hasSensitiveMetric(route.requiresSensitiveMetric, project)))
                .sort((left, right) => Number(right.report === ProjectReport.ReportingDaily) - Number(left.report === ProjectReport.ReportingDaily))
                .forEach((route) => {
                    if (route.subscriptionCostSource !== undefined) {
                        route.lockedBySubscription = !hasSubscriptionCostSource(project, route.subscriptionCostSource);
                    } else if (route.subscriptionFeature !== undefined) {
                        route.lockedBySubscription = !hasSubscriptionFeature(project, route.subscriptionFeature);
                    }
                    if (route.lockedBySubscription) {
                        route.subscriptionUpgradePath = '/project/' + project.id + '/settings/subscription';
                    }
                    const legacyPath = route.path.replace('/reporting', '/reporting-lab');

                    if (!defaultRouteSelected && !route.lockedBySubscription) {
                        routes.push({...route, path: '/'});
                        routes.push({...route, hiddenFromMenu: true});
                        routes.push({...route, path: legacyPath, hiddenFromMenu: true});
                        defaultRouteSelected = true;
                        return;
                    }

                    routes.push(route);
                    routes.push({...route, path: legacyPath, hiddenFromMenu: true});
                });

        if (isAdministrator === true) {
            routes.push(
                administratorOnlyRoute({
                    path: '/project/' + project.id + '/reporting/acquisition-executive',
                    name: t('google_reporting.menu.executive', {defaultValue: 'Akvizice a obchod'}),
                    group: t('google_reporting.menu.group', {defaultValue: 'Data a měření'}),
                    icon: <SvgIcon source={ChartIcon}/>,
                    page: <ReportingGoogleDataLab project={project} view={GoogleDataReportView.Executive}/>,
                }),
                administratorOnlyRoute({
                    path: '/project/' + project.id + '/reporting/google-analytics',
                    name: t('google_reporting.menu.overview', {defaultValue: 'Přehled GA4'}),
                    group: t('google_reporting.menu.group', {defaultValue: 'Data a měření'}),
                    icon: <SvgIcon source={ChartIcon}/>,
                    page: <ReportingGoogleDataLab project={project} view={GoogleDataReportView.Analytics}/>,
                }),
                administratorOnlyRoute({
                    path: '/project/' + project.id + '/reporting/google-analytics-acquisition',
                    name: t('google_reporting.menu.acquisition', {defaultValue: 'Akvizice'}),
                    group: t('google_reporting.menu.group', {defaultValue: 'Data a měření'}),
                    icon: <SvgIcon source={ChartIcon}/>,
                    page: <ReportingGoogleDataLab project={project} view={GoogleDataReportView.AnalyticsAcquisition}/>,
                }),
                administratorOnlyRoute({
                    path: '/project/' + project.id + '/reporting/google-analytics-content',
                    name: t('google_reporting.menu.content', {defaultValue: 'Obsah a landing pages'}),
                    group: t('google_reporting.menu.group', {defaultValue: 'Data a měření'}),
                    icon: <SvgIcon source={ChartIcon}/>,
                    page: <ReportingGoogleDataLab project={project} view={GoogleDataReportView.AnalyticsContent}/>,
                }),
                administratorOnlyRoute({
                    path: '/project/' + project.id + '/reporting/google-analytics-ecommerce',
                    name: t('google_reporting.menu.ecommerce', {defaultValue: 'E-commerce a produkty'}),
                    group: t('google_reporting.menu.group', {defaultValue: 'Data a měření'}),
                    icon: <SvgIcon source={ChartIcon}/>,
                    page: <ReportingGoogleDataLab project={project} view={GoogleDataReportView.AnalyticsEcommerce}/>,
                }),
                administratorOnlyRoute({
                    path: '/project/' + project.id + '/reporting/google-analytics-audience',
                    name: t('google_reporting.menu.audience', {defaultValue: 'Zařízení a geografie'}),
                    group: t('google_reporting.menu.group', {defaultValue: 'Data a měření'}),
                    icon: <SvgIcon source={ChartIcon}/>,
                    page: <ReportingGoogleDataLab project={project} view={GoogleDataReportView.AnalyticsAudience}/>,
                }),
                administratorOnlyRoute({
                    path: '/project/' + project.id + '/reporting/search-console',
                    name: t('google_reporting.menu.seo', {defaultValue: 'SEO výkon'}),
                    group: t('google_reporting.menu.group', {defaultValue: 'Data a měření'}),
                    icon: <SvgIcon source={ChartIcon}/>,
                    page: <ReportingGoogleDataLab project={project} view={GoogleDataReportView.SearchConsole}/>,
                }),
                administratorOnlyRoute({
                    path: '/project/' + project.id + '/reporting/merchant-catalogue',
                    name: t('google_reporting.menu.merchant', {defaultValue: 'Katalog a Shopping'}),
                    group: t('google_reporting.menu.group', {defaultValue: 'Data a měření'}),
                    icon: <SvgIcon source={ChartIcon}/>,
                    page: <ReportingGoogleDataLab project={project} view={GoogleDataReportView.Merchant}/>,
                }),
                administratorOnlyRoute({
                    path: '/project/' + project.id + '/reporting/bigquery-ga4-export',
                    name: t('google_reporting.menu.bigquery', {defaultValue: 'GA4 BigQuery detail'}),
                    group: t('google_reporting.menu.group', {defaultValue: 'Data a měření'}),
                    icon: <SvgIcon source={ChartIcon}/>,
                    page: <ReportingGoogleDataLab project={project} view={GoogleDataReportView.BigQuery}/>,
                }),
                administratorOnlyRoute({
                    path: '/project/' + project.id + '/reporting/google-attribution-diagnostics',
                    name: t('google_reporting.menu.attribution', {defaultValue: 'Diagnostika atribuce'}),
                    group: t('google_reporting.menu.group', {defaultValue: 'Data a měření'}),
                    icon: <SvgIcon source={ChartIcon}/>,
                    page: <ReportingGoogleDataLab project={project} view={GoogleDataReportView.Attribution}/>,
                }),
                administratorOnlyRoute({
                    path: '/project/' + project.id + '/reporting/google-source-ownership',
                    name: t('google_reporting.menu.ownership', {defaultValue: 'Vlastnictví zdrojů'}),
                    group: t('google_reporting.menu.group', {defaultValue: 'Data a měření'}),
                    icon: <SvgIcon source={ChartIcon}/>,
                    page: <ReportingGoogleDataLab project={project} view={GoogleDataReportView.Ownership}/>,
                }),
                administratorOnlyRoute({
                    path: '/project/' + project.id + '/reporting/advertising-formats',
                    name: t("reporting_lab.pages.advertising_classification", {defaultValue: "Strategie reklam"}),
                    group: t('route.marketing'),
                    icon: <SvgIcon source={AdIcon}/>,
                    page: <ReportingAdvertisingClassificationLab project={project} view={AdvertisingReportView.Formats}/>,
                }),
                administratorOnlyRoute({
                    path: '/project/' + project.id + '/reporting/advertising-outside-shop',
                    name: t("reporting_lab.pages.advertising_classification", {defaultValue: "Strategie reklam"}),
                    group: t('route.marketing'),
                    icon: <SvgIcon source={AdIcon}/>,
                    hiddenFromMenu: true,
                    page: <ReportingAdvertisingClassificationLab project={project} view={AdvertisingReportView.Formats}/>,
                }),
                administratorOnlyRoute({
                    path: '/project/' + project.id + '/reporting/advertising-campaign-mix',
                    name: t("reporting_lab.pages.advertising_campaign_mix", {defaultValue: "Strategie kampaní"}),
                    group: t('route.marketing'),
                    icon: <SvgIcon source={AdIcon}/>,
                    page: <ReportingAdvertisingClassificationLab project={project} view={AdvertisingReportView.CampaignMix}/>,
                }),
                {
                    path: '/project/' + project.id + '/reporting/categories-brands',
                    name: t("reporting_lab.pages.categories_brands", {defaultValue: "Kategorie a značky"}),
                    page: <ReportingCategoriesBrandsLab project={project}/>,
                    hiddenFromMenu: true
                },
                {
                    path: '/project/' + project.id + '/reporting/sales-discounts',
                    name: t("reporting_lab.pages.sales_discounts", {defaultValue: "Řádkové slevy"}),
                    page: <ReportingSalesDiscountsLab project={project}/>,
                    hiddenFromMenu: true
                },
                {
                    path: '/project/' + project.id + '/reporting-lab/categories-brands',
                    name: t("reporting_lab.pages.categories_brands", {defaultValue: "Kategorie a značky"}),
                    page: <ReportingCategoriesBrandsLab project={project}/>,
                    hiddenFromMenu: true
                },
                {
                    path: '/project/' + project.id + '/reporting-lab/sales-discounts',
                    name: t("reporting_lab.pages.sales_discounts", {defaultValue: "Řádkové slevy"}),
                    page: <ReportingSalesDiscountsLab project={project}/>,
                    hiddenFromMenu: true
                },
            );
        }

        if (project.hasErpIntegration) {
            if (hasLegacyReport(ProjectLegacyReport.DailyOverview, project)) {
                legacyReportRoutes.push({
                    path: defaultRouteSelected ? '/project/' + project.id + '/order/daily-metrics' : '/',
                    group: t('route.legacy-reports'),
                    name: t('route.daily-metrics'),
                    icon: <SvgIcon source={HistoryIcon} />,
                    page: <DailyOverview project={project}/>
                });
                defaultRouteSelected = true;
            }

            if (hasLegacyReport(ProjectLegacyReport.MainOverview, project)) {
                legacyReportRoutes.push({
                    path: defaultRouteSelected ? '/project/' + project.id + '/order/performance-summary' : '/',
                    group: t('route.legacy-reports'),
                    name: t('route.performance-summary'),
                    icon: <SvgIcon source={HistoryIcon} />,
                    page: <MainOrderOverview project={project}/>
                });

            }

            if (hasLegacyReport(ProjectLegacyReport.MarketingAndSales, project)) {
                legacyReportRoutes.push({
                    path: defaultRouteSelected ? '/project/' + project.id + '/order/marketing-and-sales' : '/',
                    group: t('route.legacy-reports'),
                    name: t('route.marketing-and-sales'),
                    icon: <SvgIcon source={HistoryIcon} />,
                    page: <NewComponents project={project}/>
                });

                defaultRouteSelected = true;
            }

            if (
                hasLegacyReport(ProjectLegacyReport.DiscountsAndCoupons, project)
                && (project?.subscription?.currentPackage?.allowDiscountCodeEvaluation ?? false)
            ) {
                legacyReportRoutes.push({
                    path: defaultRouteSelected ? '/project/' + project.id + '/order/sales-promotions' : '/',
                    group: t('route.legacy-reports'),
                    name: t('route.sales-promotions'),
                    icon: <SvgIcon source={HistoryIcon} />,
                    page: <DiscountAndCouponOverview project={project}/>
                });
                defaultRouteSelected = true;
            }

            if (hasLegacyReport(ProjectLegacyReport.CampaignPerformance, project)) {
                legacyReportRoutes.push({
                    path: defaultRouteSelected ? '/project/' + project.id + '/order/campaign-performance' : '/',
                    group: t('route.legacy-reports'),
                    name: t('route.campaign-performance'),
                    icon: <SvgIcon source={HistoryIcon} />,
                    page: <OrderMarketingChannelOverview project={project}/>
                });
                defaultRouteSelected = true;
            }

            if (
                hasPermission(ProjectPermission.Profitability, project)
                && hasSensitiveMetric(SensitiveMetric.Profit, project)
            ) {
                routes.push({
                    path: defaultRouteSelected ? '/project/' + project.id + '/order/campaign-profitability' : '/',
                    group: t('route.marketing'),
                    name: t('route.campaign-profitability'),
                    icon: <SvgIcon source={AdIcon} />,
                    page: <OrderMarketingProfitabilityOverview project={project}/>
                });
                defaultRouteSelected = true;
            }

            if (hasLegacyReport(ProjectLegacyReport.CampaignComparison, project)) {
                legacyReportRoutes.push({
                    path: defaultRouteSelected ? '/project/' + project.id + '/order/campaign-comparison' : '/',
                    group: t('route.legacy-reports'),
                    name: t('route.campaign-comparison'),
                    icon: <SvgIcon source={HistoryIcon} />,
                    page: <CampaignPerformance project={project}/>
                });
                defaultRouteSelected = true;
            }
        }

        if (project.hasLeadIntegration) {

            if (hasPermission(ProjectPermission.MainOverview, project)) {
                routes.push({
                    path: defaultRouteSelected ? '/project/' + project.id + '/lead-gen/performance-summary' : '/',
                    group: t('route.leadgen'),
                    name: t('route.performance-summary'),
                    icon: <SvgIcon source={UsersIcon} />,
                    page: <MainLeadOverview project={project}/>
                });
                defaultRouteSelected = true;
            }

            if (hasPermission(ProjectPermission.MarketingChannelOverview, project)) {
                routes.push({
                    path: defaultRouteSelected ? '/project/' + project.id + '/lead-gen/campaign-performance' : '/',
                    group: t('route.lead-marketing'),
                    icon: <SvgIcon source={AdIcon} />,
                    name: t('route.campaign-performance'),
                    page: <LeadMarketingChannelOverview project={project}/>
                });
                defaultRouteSelected = true;
            }

            // TODO: fix those graphs...
            /*if (hasPermission(ProjectPermission.MarketingChannelOverview, project)) {
                routes.push({
                    path: defaultRouteSelected ? '/project/' + project.id + '/lead-gen/lead-performance' : '/',
                    group: t('route.leadgen'),
                    name: t('route.lead-performance'),
                    page: <LeadPerformance project={project}/>
                });
                defaultRouteSelected = true;
            }*/
        }

        if (project.hasErpIntegration) {
            if (hasPermission(ProjectPermission.ProjectAffiliate, project)) {
                const costSourceAllowed = hasSubscriptionCostSource(project, "affiliate");
                routes.push({
                    path: defaultRouteSelected || !costSourceAllowed ? '/project/' + project.id + '/other-system/affiliate' : '/',
                    group: t('route.costs'),
                    name: t('route.affiliate-spends'),
                    icon: <SvgIcon source={MoneyIcon} />,
                    page: <OtherSystemSettings project={project} otherSystemType={OtherSystemType.Affiliate}/>,
                    lockedBySubscription: !costSourceAllowed,
                    subscriptionUpgradePath: '/project/' + project.id + '/settings/subscription',
                });
                defaultRouteSelected = defaultRouteSelected || costSourceAllowed;
            }

            if (hasPermission(ProjectPermission.ProjectMailing, project)) {
                const costSourceAllowed = hasSubscriptionCostSource(project, "emailing");
                routes.push({
                    path: defaultRouteSelected || !costSourceAllowed ? '/project/' + project.id + '/other-system/mailing' : '/',
                    group: t('route.costs'),
                    name: t('route.mailing-spends'),
                    icon: <SvgIcon source={MoneyIcon} />,
                    page: <OtherSystemSettings project={project} otherSystemType={OtherSystemType.Mailing}/>,
                    lockedBySubscription: !costSourceAllowed,
                    subscriptionUpgradePath: '/project/' + project.id + '/settings/subscription',
                });
                defaultRouteSelected = defaultRouteSelected || costSourceAllowed;
            }

            if (hasPermission(ProjectPermission.ProjectOfflineSpends, project)) {
                const costSourceAllowed = hasSubscriptionCostSource(project, "other_costs");
                routes.push({
                    path: defaultRouteSelected || !costSourceAllowed ? '/project/' + project.id + '/other-system/other' : '/',
                    group: t('route.costs'),
                    name: t('route.other-spends'),
                    icon: <SvgIcon source={MoneyIcon} />,
                    page: <OtherSystemSettings project={project} otherSystemType={OtherSystemType.Other}/>,
                    lockedBySubscription: !costSourceAllowed,
                    subscriptionUpgradePath: '/project/' + project.id + '/settings/subscription',
                });
                defaultRouteSelected = defaultRouteSelected || costSourceAllowed;
            }
        }

        routes.push(...legacyReportRoutes);


        if (!project.hasErpIntegration && !project.hasLeadIntegration) {
            if (hasPermission(ProjectPermission.ProjectIntegrations, project)) {
                routes.push({
                    path: defaultRouteSelected ? '/project/' + project.id + '/system-setup' : '/',
                    group: t('route.settings'),
                    name: t('route.system-setup'),
                    icon: <SvgIcon source={SettingsIcon} />,
                    page: <CreateIntegrationChoosePlatform project={project} brand={null}/>,
                });
                defaultRouteSelected = true;
            }
        }

        const projectSettingsAccess = getProjectSettingsAccess(project, hasPermission, true);
        if (projectSettingsAccess.canEditProfile
            || projectSettingsAccess.canViewBilling
            || projectSettingsAccess.canViewMcp) {
            routes.push({
                path: defaultRouteSelected ? '/project/' + project.id + '/settings' : '/',
                group: t('route.settings'),
                name: t('route.project-settings'),
                icon: <SvgIcon source={SettingsIcon} />,
                page: <ProjectSettings project={project}/>
            });
            defaultRouteSelected = true;
        }

        routes.push({
            path: '/projects',
            group: t('route.settings'),
            name: t('route.projects'),
            icon: <SvgIcon source={SettingsIcon} />,
            page: <UserProjectList/>
        });

        if (hasPermission(ProjectPermission.ProjectIntegrations, project)) {
            routes.push({
                path: '/project/' + project.id + '/settings/domains',
                group: t('route.settings'),
                name: t('route.project-domains'),
                icon: <SvgIcon source={SettingsIcon} />,
                page: <ProjectDomains project={project}/>
            });
        }

        if (hasPermission(ProjectPermission.ProjectBilling, project)) {
            const subscriptionPage = <ProjectSubscription project={project}/>;
            routes.push({
                path: '/project/' + project.id + '/settings/subscription',
                group: t('route.settings'),
                name: t('route.subscription'),
                icon: <SvgIcon source={SettingsIcon} />,
                page: subscriptionPage
            });
            routes.push({
                path: '/project/' + project.id + '/settings/plans',
                name: null,
                hiddenFromMenu: true,
                page: <Navigate replace to={'/project/' + project.id + '/settings/subscription'}/>,
            });
        }

        if (hasPermission(ProjectPermission.ProjectIntegrations, project)) {
            if (!defaultRouteSelected) {
                routes.push({
                    path: '/project/' + project.id + '/settings/integrations',
                    group: t('route.settings'),
                    name: null,
                    page: <ThirdPartyServiceIntegrations project={project}/>
                });
                routes.push({
                    path: '/',
                    group: t('route.settings'),
                    name: t('project.3rd-party-integrations'),
                    page: <ThirdPartyServiceIntegrations project={project}/>
                });
                defaultRouteSelected = true;
            } else {
                routes.push({
                    path: '/project/' + project.id + '/settings/integrations',
                    group: t('route.settings'),
                    name: t('project.3rd-party-integrations'),
                    page: <ThirdPartyServiceIntegrations project={project}/>
                });
            }

            routes.push({
                path: '/project/' + project.id + '/settings/integrations/create',
                group: t('route.settings'),
                name: null,
                page: <CreateIntegrationChoosePlatform project={project} brand={null}/>
            });

            for (let brand of Object.values(brands)) {
                routes.push({
                    path: '/project/' + project.id + '/settings/integrations/create/' + brand['slug'],
                    group: t('route.settings'),
                    name: null,
                    page: <CreateIntegrationChoosePlatform project={project} brand={brand}/>
                });
            }

            if (project.hasErpIntegration && hasPermission(ProjectPermission.ProjectOrderStatuses, project)) {
                routes.push({
                    path: '/project/' + project.id + '/settings/order-statuses',
                    group: t('route.settings'),
                    name: t('project.order-statuses'),
                    page: <OrderStatusSettings project={project}/>
                });
            }
        }

        if (hasPermission(ProjectPermission.ProjectUsers, project)) {
            routes.push({
                path: '/project/' + project.id + '/settings/users',
                group: t('route.settings'),
                name: t('route.project-users'),
                page: <ProjectUsers project={project}/>
            });
        }

        if (project.isOwner || context.currentIdentity.user.isAdministrator) {
            routes.push({
                path: defaultRouteSelected || !hasSubscriptionFeature(project, "roles_permissions") ? '/project/' + project.id + '/roles' : '/',
                group: t('route.settings'),
                name: t('route.project-user-roles'),
                page: <ProjectRoles project={project}/>,
                lockedBySubscription: !hasSubscriptionFeature(project, "roles_permissions"),
                subscriptionUpgradePath: '/project/' + project.id + '/settings/subscription',
            });
            defaultRouteSelected = defaultRouteSelected || hasSubscriptionFeature(project, "roles_permissions");
        }

    }

    if (routes.length === 0) {
        routes.push({
            // Base Route
            path: '/',
            name: t('route.dashboard'),
            icon: <SvgIcon source={DashboardIcon} />,
            page: <NoProjectScreen project={currentProject}/>
        });
    }

    return [routes, lastProjectName];
}

export function useAdminRoutes() {
    const {t} = useTranslation();
    const isAdministrator = useIsAdministrator();
    const {context} = useContext(GlobalContext);
    const currentProject = (context.currentUserProjects ?? []).find((project) => project.id === context.currentProjectId);


    let adminRoutes = [];

    if (isAdministrator === true) {
        adminRoutes = [
            {
                path: '/admin/projects/status',
                group: t('route.administration'),
                name: t('route.service-status'),
                icon: <SvgIcon source={ShieldUserIcon} />,
                page: <ServiceStatus/>
            },
            {
                path: '/admin/subscription-packages',
                group: t('route.administration'),
                name: t("admin.subscription_packages.title", {defaultValue: "Balíčky"}),
                icon: <SvgIcon source={MoneyIcon} />,
                page: <SubscriptionPackages/>
            },
            {
                path: '/admin/subscription-payments',
                group: t('route.administration'),
                name: t("admin.subscription_payments.title", {defaultValue: "Platby"}),
                icon: <SvgIcon source={MoneyIcon} />,
                page: <SubscriptionPayments/>
            },
            ...(currentProject ? [
                {
                    path: '/project/' + currentProject.id + '/admin/tracking-quality',
                    group: t('route.administration'),
                    name: t("reporting_lab.pages.tracking_quality", {defaultValue: "Měření a párování"}),
                    icon: <SvgIcon source={LinksIcon} />,
                    page: <ReportingTrackingQualityLab project={currentProject}/>
                },
                {
                    path: '/project/' + currentProject.id + '/admin/data-quality',
                    group: t('route.administration'),
                    name: t("reporting_lab.pages.data_quality", {defaultValue: "Datová kvalita"}),
                    icon: <SvgIcon source={ChartIcon} />,
                    page: <ReportingDataQualityLab project={currentProject}/>
                },
                {
                    path: '/project/' + currentProject.id + '/reporting-lab/tracking-quality',
                    name: t("reporting_lab.pages.tracking_quality", {defaultValue: "Měření a párování"}),
                    page: <ReportingTrackingQualityLab project={currentProject}/>,
                    hiddenFromMenu: true
                },
                {
                    path: '/project/' + currentProject.id + '/reporting-lab/data-quality',
                    name: t("reporting_lab.pages.data_quality", {defaultValue: "Datová kvalita"}),
                    page: <ReportingDataQualityLab project={currentProject}/>,
                    hiddenFromMenu: true
                },
            ] : []),
            /* {
                path: '/admin/projects/list',
                group: t('route.projects'),
                name: t('route.project-list'),
                page: <UnderConstruction/>
            }, {
                path: '/admin/users',
                name: t('route.user-list'),
                icon: <FontAwesomeIcon icon={fas.faUsers}/>,
                page: <UnderConstruction/>
            }, {
                path: '/service-status/workers',
                name: t('route.worker-status'),
                icon: <FontAwesomeIcon icon={fas.faHeartPulse}/>,
                page: <ServiceStatus/>
            }*/
        ];
    }

    return [markRoutesAdministratorOnly(adminRoutes)];
}

export function useHelpAndLegalRoutes() {
    const {t} = useTranslation();

    return [
        [
            {
                href: 'https://datixo.freshdesk.com/support/solutions',
                name: t('route.knowledge-base'),
                icon: <SvgIcon source={BookIcon} />,
            },
            {
                href: 'https://datixo.freshdesk.com/support/tickets/new',
                name: t('route.helpdesk'),
                icon: <SvgIcon source={HelpdeskIcon} />,
            }

            /*{
                path: '/help/faq',
                group: t('route.help'),
                name: t('route.frequently-asked-questions'),
                icon: <FontAwesomeIcon icon={fas.faLifeRing}/>,
                page: <UnderConstruction/>
            },

            {
                path: '/support/tickets',
                group: t('route.help'),
                name: t('route.support-ticket'),
                page: <UnderConstruction/>
            },
            {
                path: '/legal/terms-of-service',
                name: t('route.terms-of-service'),
                icon: <FontAwesomeIcon icon={fas.faFileContract}/>,
                page: <UnderConstruction/>
            },
            {
                path: '/legal/privacy-policy',
                name: t('route.privacy-policy'),
                icon: <FontAwesomeIcon icon={fas.faSection}/>,
                page: <UnderConstruction/>
            },*/
        ]];
}

export function useOnboardingRoutes() {
    const {t} = useTranslation();

    const navigate = useNavigate();

    return [[
        {
            path: '/onboarding/password',
            name: t('user.set-password'),
            page: <SetPasswordStep/>
        },
        {
            path: '/onboarding/info',
            name: t('user.basic-data'),
            page: <AccountNameStep/>
        },
        {
            path: '/onboarding/project',
            name: t('project.create'),
            page: <ProjectStep/>
        },
        {
            path: '/onboarding/addon-project',
            name: t('onboarding.connect-addon'),
            page: <AddonProjectStep/>
        },
    ]];
}

export function useAuthenticationRoutes() {
    const {t} = useTranslation();

    let [onboardingRoutes] = useOnboardingRoutes();

    return [[
        {
            path: '/sign/in', name: t('auth.sign-in'), page: <SignIn/>
        },
        {
            path: '/oauth/authorize', name: t('route.mcp-oauth-consent', {defaultValue: 'MCP OAuth consent'}), page: <McpAuthorizationConsent/>
        },
        {
            path: '/sign/out', name: t('auth.sign-out'), page: <SignOut/>
        },
        {
            path: '/logout', name: t('auth.sign-out'), page: <SignOut/>
        },
        {
            path: '/sign/up', name: t('auth.sign-up'), page: <SignUp/>
        },
        {
            path: '/sign/in/addon', name: t('onboarding.connect-addon'), page: <AddonAuth/>
        },
        {
            path: '/sign/up/continue-with-email', name: t('auth.sign-up'), page: <SignUpGoToEmail/>
        },
        {
            path: '/reset-password', name: t('auth.reset-password'), page: <ResetPassword/>
        },
        {
            path: '/sign/up/email', name: t('auth.confirm-email'), page: <ContinueFromEmail/>
        },
        {
            path: '/invite', name: t('auth.invitation-confirmation'), page: <ContinueFromInvitation/>
        },
        ...onboardingRoutes
    ]];
}

export function useUserRoutes() {
    const {t} = useTranslation();

    return [
        [
            {
                path: '/settings/profile',
                name: 'account-settings-profile-redirect',
                page: <Navigate replace to="/projects"/>
            },
            {
                path: '/settings/password',
                name: 'account-settings-password-redirect',
                page: <Navigate replace to="/projects"/>
            },
            /*{
                path: '/settings/connected-accounts',
                group: t('user.account-settings'),
                name: t('user.connected-accounts'),
                icon: <FontAwesomeIcon icon={fas.faCircleNodes}/>,
                page: <UserSettingsConstruction/>
            },*/
            {
                path: '/projects',
                group: t('user.projects'),
                name: t('user.project-list'),
                icon: <SvgIcon source={SettingsIcon} />,
                page: <UserProjectList/>
            },
            /*{
                path: '/feedback',
                group: t('user.experience'),
                name: t('user.give-feedback'),
                icon: <FontAwesomeIcon icon={fas.faCommentDots}/>,
                page: <UserSettingsConstruction/>
            },*/
        ]
    ];
}

export function useRouteList() {

    let [projectRoutes] = useProjectRoutes();

    let [adminRoutes] = useAdminRoutes();

    let [helpAndLegalRoutes] = useHelpAndLegalRoutes();

    let [authenticationRoutes] = useAuthenticationRoutes();

    let [userRoutes] = useUserRoutes();

    return [
        projectRoutes
            .concat(adminRoutes)
            .concat(helpAndLegalRoutes)
            .concat(authenticationRoutes)
            .concat(userRoutes)
    ];
}

function Routing() {
    const [routeList] = useRouteList();

    return (<Routes>
        {routeList
            .filter((route) => {
                return route['page'] !== undefined;
            })
            .map((route) => {
                const page = route.lockedBySubscription && route.subscriptionUpgradePath
                    ? <Navigate replace to={route.subscriptionUpgradePath}/>
                    : route.page;

                return (<Route path={route['path']} element={page} key={(route['name'] + route['path'])}/>);
            })
        }
        <Route path="*" element={<NotFound/>}/>
    </Routes>);
}

export default Routing;
